import { action, computed, makeObservable, observable } from 'mobx';
import { Service } from 'typedi';

import type { IApiError } from '../services/apiError';
import {
  AnnouncementStatus,
  type IAnnouncementEntity,
  type IAnnouncementLocalization,
  type IRedirect,
} from '../types/announcement';
import { newLocalizations, toLocalInput } from '../utils/announcement';
import { isBusinessVersion, type TValidationReason, validate } from '../utils/validation';

export type TVersionCardConfirm = 'publish' | 'edit' | 'withdraw' | null;

@Service()
export class VersionCardStore {
  private _card: IAnnouncementEntity | undefined = undefined;
  private _version = '';
  private _pinned = false;
  private _important = false;
  private _localizations: IAnnouncementLocalization[] = newLocalizations();
  /** datetime-local value in the manager's local time. */
  private _publishAt = '';
  private _busy = false;
  private _serverError: IApiError | null = null;
  private _confirming: TVersionCardConfirm = null;
  private _notifyAgain = false;
  private _redirect: IRedirect | null = null;

  constructor() {
    makeObservable<
      VersionCardStore,
      | '_card'
      | '_version'
      | '_pinned'
      | '_important'
      | '_localizations'
      | '_publishAt'
      | '_busy'
      | '_serverError'
      | '_confirming'
      | '_notifyAgain'
      | '_redirect'
    >(this, {
      _card: observable.ref,
      _version: observable,
      _pinned: observable,
      _important: observable,
      _localizations: observable.ref,
      _publishAt: observable,
      _busy: observable,
      _serverError: observable.ref,
      _confirming: observable,
      _notifyAgain: observable,
      _redirect: observable.ref,
      card: computed,
      version: computed,
      pinned: computed,
      important: computed,
      localizations: computed,
      publishAt: computed,
      busy: computed,
      serverError: computed,
      confirming: computed,
      notifyAgain: computed,
      redirect: computed,
      scheduled: computed,
      published: computed,
      readOnly: computed,
      reason: computed,
      time: computed,
      init: action,
      show: action,
      setVersion: action,
      setPinned: action,
      setImportant: action,
      setLocalizations: action,
      setPublishAt: action,
      startAction: action,
      finishAction: action,
      setServerError: action,
      setConfirming: action,
      setNotifyAgain: action,
      setRedirect: action,
      clearRedirect: action,
    });
  }

  get card(): IAnnouncementEntity | undefined {
    return this._card;
  }
  get version(): string {
    return this._version;
  }
  get pinned(): boolean {
    return this._pinned;
  }
  get important(): boolean {
    return this._important;
  }
  get localizations(): IAnnouncementLocalization[] {
    return this._localizations;
  }
  get publishAt(): string {
    return this._publishAt;
  }
  get busy(): boolean {
    return this._busy;
  }
  get serverError(): IApiError | null {
    return this._serverError;
  }
  get confirming(): TVersionCardConfirm {
    return this._confirming;
  }
  get notifyAgain(): boolean {
    return this._notifyAgain;
  }
  get redirect(): IRedirect | null {
    return this._redirect;
  }
  get scheduled(): boolean {
    return this._card?.status === AnnouncementStatus.ANNOUNCEMENT_STATUS_SCHEDULED;
  }
  get published(): boolean {
    return this._card?.status === AnnouncementStatus.ANNOUNCEMENT_STATUS_PUBLISHED;
  }
  get readOnly(): boolean {
    const status = this._card?.status;
    return (
      status === AnnouncementStatus.ANNOUNCEMENT_STATUS_WITHDRAWN ||
      status === AnnouncementStatus.ANNOUNCEMENT_STATUS_EXPIRED
    );
  }
  get reason(): TValidationReason | null {
    const version = this._version.trim();
    if (!version) return 'validation.version';
    if (!isBusinessVersion(version)) return 'validation.versionFormat';
    return validate(this._localizations);
  }
  /** When a scheduled card goes out, or when a published one went out. */
  get time(): Date | undefined {
    return this.scheduled ? this._card?.publishAt : this._card?.publishedAt;
  }

  /** A new visit: a new card (no `card`) or an existing one. */
  init = (card?: IAnnouncementEntity): void => {
    this._card = card;
    this._version = card?.businessVersion ?? '';
    this._pinned = card?.pinned ?? false;
    this._important = card?.important ?? false;
    this._localizations = card?.localizations ?? newLocalizations();
    this._publishAt = toLocalInput(card?.publishAt);
    this._busy = false;
    this._serverError = null;
    this._confirming = null;
    this._notifyAgain = false;
    this._redirect = null;
  };
  /** The stored card, updated in place. */
  show = (card: IAnnouncementEntity): void => {
    this._card = card;
    this._localizations = card.localizations;
    this._pinned = card.pinned;
    this._important = card.important;
    this._publishAt = toLocalInput(card.publishAt);
  };
  setVersion = (version: string): void => {
    this._version = version;
  };
  setPinned = (pinned: boolean): void => {
    this._pinned = pinned;
  };
  setImportant = (important: boolean): void => {
    this._important = important;
  };
  setLocalizations = (localizations: IAnnouncementLocalization[]): void => {
    this._localizations = localizations;
  };
  setPublishAt = (publishAt: string): void => {
    this._publishAt = publishAt;
  };
  startAction = (): void => {
    this._busy = true;
    this._serverError = null;
  };
  finishAction = (): void => {
    this._busy = false;
  };
  setServerError = (error: IApiError): void => {
    this._serverError = error;
  };
  setConfirming = (confirming: TVersionCardConfirm): void => {
    this._confirming = confirming;
  };
  setNotifyAgain = (notifyAgain: boolean): void => {
    this._notifyAgain = notifyAgain;
  };
  setRedirect = (redirect: IRedirect): void => {
    this._redirect = redirect;
  };
  clearRedirect = (): void => {
    this._redirect = null;
  };
}
